'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { logger } from '@/lib/platform/utils/logger';
import { Organization } from '../types';
import { useSession } from '../auth/client';
import { useOrganizationSelection } from '@/lib/platform/hooks/use-organization-preferences';
import { useOrganizationQuery } from '@/lib/platform/hooks/queries/use-organization-query';
import { storage } from '@/lib/platform/utils/storage';
import { ORG_KEYS } from '@/lib/platform/constants/storage-keys';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';

interface OrganizationContextType {
  organization: Organization | null | undefined;
  logoUrl: string | null;
  isLoadingOrganization: boolean;
  refreshOrganization: () => Promise<void>;
  updateLogoUrl: (url: string | null) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: session, isPending: isSessionPending } = useSession();
  const { selectedOrganizationId, setSelectedOrganization } = useOrganizationSelection();

  // State
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Refs to prevent infinite loops
  const hasSyncedSessionOrg = useRef(false);

  // ============================================================================
  // FIX for v2 infinite loop: Handle ?org= parameter in SEPARATE useEffect
  // UPDATED: Check for org parameter on every pathname change to handle onboarding redirect
  // ============================================================================
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    const fromOnboarding = urlParams.get('fromOnboarding') === 'true';

    // Process org parameter if present (especially important after onboarding)
    if (orgParam && orgParam !== selectedOrganizationId) {
      logger.log(
        '[OrganizationProvider] Found org parameter in URL, switching to organization:',
        orgParam,
        { fromOnboarding }
      );
      setSelectedOrganization(orgParam);
      // DON'T remove org parameter yet - wait until organization is loaded
    }
  }, [selectedOrganizationId, setSelectedOrganization]); // Run when selectedOrganizationId changes

  // ============================================================================
  // Seed localStorage org selection from Better Auth's activeOrganizationId.
  //
  // WHY localStorage (and not React Query / session state): the selected
  // organization id is ephemeral UI selection state that must survive a full
  // page reload BEFORE the Better Auth session has loaded — React Query's
  // cache is in-memory and does not survive reloads. This is the documented
  // exception to the "no localStorage caching" rule (CLAUDE.md): it is a
  // cross-reload selection flag, not data caching. Organization DATA is
  // fetched via useOrganizationQuery (React Query) below.
  //
  // Direction: session → localStorage, and only when localStorage is empty
  // (fresh login / new browser). A user-chosen org in localStorage is never
  // overwritten by the session value. Reviewed 2026-10-07: legitimate, keep.
  // ============================================================================
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      hasSyncedSessionOrg.current ||
      isSessionPending ||
      !session?.session?.activeOrganizationId
    )
      return;

    const sessionOrgId = session.session.activeOrganizationId;
    const currentSelectedOrgId = storage.get<string>(ORG_KEYS.SELECTED_ORGANIZATION);

    // If session has activeOrganizationId but localStorage doesn't, sync them
    if (!currentSelectedOrgId && sessionOrgId) {
      logger.log(
        '[OrganizationProvider] Syncing localStorage with session activeOrganizationId:',
        sessionOrgId
      );
      setSelectedOrganization(sessionOrgId);
      hasSyncedSessionOrg.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isSessionPending]);

  // ============================================================================
  // React Query hook for fetching organization data
  // This replaces all the manual sessionStorage cache logic
  // ============================================================================
  const { data, isLoading, isFetching, error } = useOrganizationQuery(selectedOrganizationId);

  // Update logo URL when organization data changes — adjust state during render instead
  // of in an effect (https://react.dev/learn/you-might-not-need-an-effect)
  const [prevData, setPrevData] = useState<typeof data | undefined>(undefined);
  if (prevData !== data) {
    setPrevData(data);
    if (data?.logo_url !== undefined) {
      setLogoUrl(data.logo_url || null);
    } else if (data === null) {
      setLogoUrl(null);
    }
  }

  // ============================================================================
  // Remove ?org= parameter from URL once organization is loaded successfully
  // This prevents infinite redirect loop during onboarding
  // ============================================================================
  useEffect(() => {
    if (typeof window === 'undefined' || !data || isLoading || isFetching) return;

    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');

    // Only remove if org parameter matches the loaded organization
    if (orgParam && orgParam === data.id) {
      logger.log('[OrganizationProvider] Organization loaded, removing org parameter from URL');
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('org');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [data, isLoading, isFetching]); // Run when organization data loads

  // REMOVED: This was causing redirect loop during onboarding
  // The organization might not exist yet while Stripe webhook is processing
  // It's better to show loading state than to aggressively clear the ID
  // useEffect(() => {
  //   if (data === null && selectedOrganizationId) {
  //     logger.log('[OrganizationProvider] Clearing invalid organization ID');
  //     setSelectedOrganization(null);
  //   }
  // }, [data, selectedOrganizationId]);

  // Store selected organization ID when organization is loaded.
  // Also self-heals a stale stored ID: if the loaded organization differs from
  // the stored ID (e.g. the query fell back to the user's own org after a 403
  // caused by impersonation or removed membership), sync the stored ID to the
  // organization that actually loaded. Unlike the removed "clear on null"
  // effect, this only runs when real data exists, so it cannot fire during
  // onboarding while the Stripe webhook is still creating the org.
  useEffect(() => {
    if (data && data.id !== selectedOrganizationId) {
      logger.log('[OrganizationProvider] Syncing stored organization ID to loaded data:', data.id);
      setSelectedOrganization(data.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedOrganizationId]);

  // ============================================================================
  // Context methods
  // ============================================================================

  const refreshOrganization = useCallback(async () => {
    logger.log('[OrganizationProvider] refreshOrganization called');
    // Invalidate React Query cache to trigger refetch
    await queryClient.invalidateQueries({
      queryKey: queryKeys.organization.detail(selectedOrganizationId),
    });
    logger.log('[OrganizationProvider] refreshOrganization complete');
  }, [queryClient, selectedOrganizationId]);

  const updateLogoUrl = useCallback(
    (url: string | null) => {
      setLogoUrl(url);
      // Update organization in React Query cache
      if (data) {
        const updatedOrganization = { ...data, logo_url: url ?? undefined };
        queryClient.setQueryData(
          queryKeys.organization.detail(selectedOrganizationId),
          updatedOrganization
        );
      }
    },
    [data, queryClient, selectedOrganizationId]
  );

  // ============================================================================
  // Context value
  // ============================================================================

  const contextValue = useMemo(() => {
    // FIX: Prevent redirect loop during onboarding completion
    // Use React Query's isLoading + isFetching to keep loading state true
    // while retrying to fetch organization (handles Stripe webhook delay)
    const loadingState = isSessionPending || isLoading || isFetching;

    logger.log('[OrganizationContext] Context value update:', {
      selectedOrganizationId,
      hasData: !!data,
      dataIsNull: data === null,
      isSessionPending,
      isLoading,
      isFetching,
      finalLoadingState: loadingState,
    });

    return {
      organization: data ?? undefined, // Convert null to undefined for backwards compatibility
      logoUrl,
      isLoadingOrganization: loadingState,
      refreshOrganization,
      updateLogoUrl,
    };
    // NOTE: isFetching intentionally excluded from dependencies to prevent unnecessary re-renders
    // during background refetches. Context should only update when data actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    logoUrl,
    isSessionPending,
    isLoading,
    selectedOrganizationId,
    refreshOrganization,
    updateLogoUrl,
  ]);

  // Log errors
  useEffect(() => {
    if (error) {
      logger.error('[OrganizationProvider] Error loading organization:', error);
    }
  }, [error]);

  return (
    <OrganizationContext.Provider value={contextValue}>{children}</OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
